import { dateFromKey, dateKeyOf } from "./schedule";
import type { DietCalorieKind, DietCheerKind, DietGoal, DietLog, DietMealKind } from "./types";

/** 応援スタンプの並び順と読み。一次情報はここだけに置く(絵は画面側が持つ) */
export const DIET_CHEER_KINDS: { value: DietCheerKind; label: string }[] = [
  { value: "thumbs", label: "えらい" },
  { value: "flame", label: "その調子" },
  { value: "heart", label: "すき" },
];

export function dietCheerLabelOf(kind: DietCheerKind): string {
  return DIET_CHEER_KINDS.find((cheer) => cheer.value === kind)?.label ?? kind;
}

/** 食事欄の並び順と読み。カロリー欄(calorieKey)もここで対応させる。一次情報はここだけに置く */
export const DIET_MEAL_KINDS: {
  key: DietMealKind;
  calorieKey: DietCalorieKind;
  label: string;
}[] = [
  { key: "mealBreakfast", calorieKey: "calorieBreakfast", label: "朝" },
  { key: "mealLunch", calorieKey: "calorieLunch", label: "昼" },
  { key: "mealDinner", calorieKey: "calorieDinner", label: "晩" },
  { key: "mealSnack", calorieKey: "calorieSnack", label: "間食" },
];

/**
 * 表示用に食事欄を並べる。
 *
 * 朝・昼・晩・間食のどれかが入っていればそれだけを返す。1つも無い場合だけ、
 * 分割前の記録に残る旧 meal 列をラベル無しで1件返す(データを失わないため)。
 */
export function dietMealEntriesOf(log: DietLog): { label: string; text: string }[] {
  const entries = DIET_MEAL_KINDS.filter(({ key }) => log[key] !== null && log[key] !== "").map(
    ({ key, label }) => ({ label, text: log[key] as string }),
  );
  if (entries.length > 0) return entries;
  return log.meal !== null && log.meal !== "" ? [{ label: "", text: log.meal }] : [];
}

/**
 * その日の合計摂取カロリー(kcal)。4欄の合算。
 * 1件も入力していない日は「記録していない」であって 0kcal ではないので、null を返す。
 */
export function dietCalorieTotalOf(log: DietLog): number | null {
  const values = DIET_MEAL_KINDS.map(({ calorieKey }) => log[calorieKey]).filter(
    (value): value is number => value !== null,
  );
  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
}

/** その人の最新の記録。日付が新しい順に並んでいなくても正しく返す */
export function latestDietLogOf(logs: readonly DietLog[], authorId: string): DietLog | null {
  let latest: DietLog | null = null;
  for (const log of logs) {
    if (log.authorId !== authorId) continue;
    if (latest === null || log.loggedOn > latest.loggedOn) latest = log;
  }
  return latest;
}

/**
 * 開始からの減量(kg)。増えていれば負の数になる。
 * 記録が1件も無ければ0(まだ何も減っていない)。
 */
export function dietLossOf(goal: DietGoal | null, latest: DietLog | null): number {
  if (goal === null || latest === null) return 0;
  return goal.startWeight - latest.weight;
}

/**
 * 目標に対する進み具合(0〜1)。
 * 開始体重と目標体重が同じ(減量幅0)なら、割り算にせず達成扱いにする。
 */
export function dietProgressOf(goal: DietGoal | null, latest: DietLog | null): number {
  if (goal === null) return 0;
  const target = goal.startWeight - goal.goalWeight;
  if (target <= 0) return 1;
  return Math.min(1, Math.max(0, dietLossOf(goal, latest) / target));
}

const DAY_MS = 24 * 60 * 60 * 1000;

function previousDateKey(key: string): string {
  const date = dateFromKey(key);
  return dateKeyOf(new Date(date.getTime() - DAY_MS));
}

/**
 * 二人そろって記録した連続日数。
 *
 * 交換日記のストリーク(diaryStreakOf)と違い、**両方**が記録した日だけ伸ばす。
 * 片方だけで伸びると「相手のぶんも声をかける」動機が消えるため。
 * 今日ぶんがまだ揃っていなくても、前日までの連続は0にしない
 * (一日の途中で「途切れた」と見えると、その日の記録をやめてしまう)。
 */
export function dietPairStreakOf(
  logs: readonly DietLog[],
  selfId: string,
  partnerId: string,
  todayKey: string,
): number {
  const selfDates = new Set(
    logs.filter((log) => log.authorId === selfId).map((log) => log.loggedOn),
  );
  const partnerDates = new Set(
    logs.filter((log) => log.authorId === partnerId).map((log) => log.loggedOn),
  );
  const bothOn = (key: string) => selfDates.has(key) && partnerDates.has(key);

  let cursor = bothOn(todayKey) ? todayKey : previousDateKey(todayKey);
  let streak = 0;
  while (bothOn(cursor)) {
    streak++;
    cursor = previousDateKey(cursor);
  }
  return streak;
}

/** 今日から遡って `days` 日ぶんの日付(古い順)。グラフの横軸に使う */
export function dietDateRange(todayKey: string, days: number): string[] {
  const keys: string[] = [];
  let cursor = todayKey;
  for (let i = 0; i < days; i++) {
    keys.unshift(cursor);
    cursor = previousDateKey(cursor);
  }
  return keys;
}
